import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
    Plus, Pencil, Trash, UploadSimple, FileText, MagnifyingGlass,
    ArrowsClockwise, Sparkle, X, Copy, ImageSquare, Film, PaperPlaneTilt,
    Calendar, FacebookLogo, InstagramLogo, TiktokLogo, LinkedinLogo, YoutubeLogo,
    ThreadsLogo, Lightbulb,
} from "@phosphor-icons/react";
import SourcesTab from "./content-studio/SourcesTab";
import BrandProfileCard from "./content-studio/BrandProfileCard";
import KnowledgeBaseCard from "./content-studio/KnowledgeBaseCard";
import BufferChannelsPanel from "../components/BufferChannelsPanel";
import SocialTemplatesCard from "./content-studio/SocialTemplatesCard";
import ProductionLine from "./content-studio/ProductionLine";

// ---------- shared formatting helpers ----------

const fmtDate = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const fmtDateTime = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const PLATFORM_META = {
    Facebook:  { Icon: FacebookLogo,  tone: "text-[#1877F2]" },
    Instagram: { Icon: InstagramLogo, tone: "text-[#E4405F]" },
    TikTok:    { Icon: TiktokLogo,    tone: "text-foreground" },
    LinkedIn:  { Icon: LinkedinLogo,  tone: "text-[#0A66C2]" },
    Threads:   { Icon: ThreadsLogo,   tone: "text-foreground" },
    YouTube:   { Icon: YoutubeLogo,   tone: "text-[#FF0000]" },
};

const STATUS_TONE = {
    Idea:      "bg-muted text-muted-foreground",
    Draft:     "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
    Approved:  "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200",
    Queued:    "bg-purple-100 text-purple-900 dark:bg-purple-900/30 dark:text-purple-200",
    Published: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200",
    Archived:  "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
};

function StatusChip({ status }) {
    const tone = STATUS_TONE[status] || "bg-muted text-muted-foreground";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider ${tone}`}>
            {status || "—"}
        </span>
    );
}

const BUFFER_STATUS_TONE = {
    queued:    "bg-purple-100 text-purple-900 dark:bg-purple-900/30 dark:text-purple-200",
    published: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200",
    failed:    "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200",
};

function BufferStatusChip({ status }) {
    if (!status) return <span className="text-muted-foreground text-[11px] italic">—</span>;
    const tone = BUFFER_STATUS_TONE[status] || "bg-muted text-muted-foreground";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider ${tone}`}>
            {status}
        </span>
    );
}

function PlatformChip({ platform }) {
    if (!platform) return <span className="text-muted-foreground text-[11px] italic">—</span>;
    const meta = PLATFORM_META[platform] || {};
    const Icon = meta.Icon;
    return (
        <span className="inline-flex items-center gap-1 text-[12px]">
            {Icon && <Icon size={13} weight="fill" className={meta.tone} />}
            <span>{platform}</span>
        </span>
    );
}

// ---------- Dashboard tab ----------

function StatCard({ label, value, testid }) {
    return (
        <div data-testid={testid} className="border border-border bg-card rounded-sm px-4 py-3.5">
            <div className="overline text-[9px] mb-1.5">{label}</div>
            <div className="font-display font-black text-foreground text-[26px] tabular-nums leading-none">{value}</div>
        </div>
    );
}

function DashboardTab({ data, onJump }) {
    if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
    const T = data.totals;
    const a = data.analytics;
    return (
        <div className="space-y-10">
            <section data-testid="cs-buffer-channels">
                <BufferChannelsPanel compact />
            </section>

            <section data-testid="cs-stats">
                <div className="overline mb-3">Content totals</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard label="Total" value={T.total} testid="stat-total" />
                    <StatCard label="Ideas" value={T.Idea} testid="stat-idea" />
                    <StatCard label="Drafts" value={T.Draft} testid="stat-draft" />
                    <StatCard label="Approved" value={T.Approved} testid="stat-approved" />
                    <StatCard label="Queued" value={T.Queued} testid="stat-queued" />
                    <StatCard label="Published" value={T.Published} testid="stat-published" />
                </div>
            </section>

            <section data-testid="cs-platform-dist">
                <div className="overline mb-3">Platform distribution</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {["Facebook", "Instagram", "TikTok", "LinkedIn", "Threads", "YouTube"].map((p) => {
                        const meta = PLATFORM_META[p];
                        const Icon = meta.Icon;
                        return (
                            <div key={p} className="border border-border bg-card rounded-sm px-4 py-3.5">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Icon size={14} weight="fill" className={meta.tone} />
                                    <div className="overline text-[9px]">{p}</div>
                                </div>
                                <div className="font-display font-black text-[22px] tabular-nums leading-none">{data.platform_distribution[p] || 0}</div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section data-testid="cs-recent-activity">
                <div className="overline mb-3">Recent activity</div>
                {data.recent_activity.length === 0 ? (
                    <div className="border border-dashed border-border rounded-sm py-10 text-center text-[13px] italic text-muted-foreground">
                        Nothing yet. Create your first content piece in the Queue tab.
                    </div>
                ) : (
                    <ul className="border border-border bg-card rounded-sm divide-y divide-border">
                        {data.recent_activity.map((c) => (
                            <li
                                key={c.id}
                                className="px-4 py-2.5 flex items-center gap-3 hover:bg-muted/30 cursor-pointer"
                                onClick={() => onJump?.(c.id)}
                                data-testid={`recent-${c.id}`}
                            >
                                <StatusChip status={c.status} />
                                <div className="flex-1 min-w-0">
                                    <div className="font-display text-[13px] truncate">{c.title}</div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {c.topic || "—"} · {c.post_type || "—"} · updated {fmtDateTime(c.updated_at)}
                                    </div>
                                </div>
                                <PlatformChip platform={c.platform} />
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section data-testid="cs-upcoming">
                <div className="overline mb-3 flex items-center gap-1.5"><Calendar size={11} /> Upcoming scheduled</div>
                {data.upcoming_scheduled.length === 0 ? (
                    <div className="border border-dashed border-border rounded-sm py-8 text-center text-[12px] italic text-muted-foreground">
                        Nothing queued. Move a piece to &ldquo;Queued&rdquo; in the Queue tab to see it here.
                    </div>
                ) : (
                    <ul className="border border-border bg-card rounded-sm divide-y divide-border">
                        {data.upcoming_scheduled.map((c) => (
                            <li key={c.id} className="px-4 py-2.5 flex items-center gap-3"
                                onClick={() => onJump?.(c.id)}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="font-display text-[13px] truncate">{c.title}</div>
                                    <div className="text-[11px] text-muted-foreground">{c.scheduled_for ? `Scheduled ${fmtDateTime(c.scheduled_for)}` : "No schedule set"}</div>
                                </div>
                                <PlatformChip platform={c.platform} />
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section data-testid="cs-analytics">
                <div className="overline mb-3">Performance · placeholder for v1</div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {[["Views", a.views], ["Likes", a.likes], ["Comments", a.comments], ["Shares", a.shares], ["Clicks", a.clicks]].map(([k, v]) => (
                        <div key={k} className="border border-border bg-card rounded-sm px-4 py-3.5 opacity-60">
                            <div className="overline text-[9px] mb-1.5">{k}</div>
                            <div className="font-display font-black text-foreground text-[22px] tabular-nums leading-none">{v}</div>
                        </div>
                    ))}
                </div>
                <div className="text-[11px] italic text-muted-foreground mt-3">
                    Pulled from connected channels once Buffer integration is enabled in Settings.
                </div>
            </section>
        </div>
    );
}

// ---------- Editor dialog ----------

function CharCount({ value, limit }) {
    const n = (value || "").length;
    const over = limit && n > limit;
    return <span className={`font-mono text-[10px] tabular-nums ${over ? "text-accent" : "text-muted-foreground"}`}>{n}{limit ? ` / ${limit}` : ""}</span>;
}

function MediaTile({ asset, onDelete }) {
    const isImage = asset.asset_type === "image";
    const isVideo = asset.asset_type === "video";
    const src = `${api.defaults.baseURL}/content-studio/media/${asset.id}/file`;
    return (
        <div className="relative border border-border bg-muted/40 rounded-sm overflow-hidden group" data-testid={`media-tile-${asset.id}`}>
            {isImage && <img src={src} alt={asset.file_name} className="w-full h-32 object-cover" />}
            {isVideo && (
                <video src={src} className="w-full h-32 object-cover" muted />
            )}
            {!isImage && !isVideo && (
                <div className="w-full h-32 flex items-center justify-center text-muted-foreground">
                    <FileText size={28} />
                </div>
            )}
            <button
                type="button"
                onClick={() => onDelete(asset.id)}
                className="absolute top-1 right-1 bg-background/85 hover:bg-accent hover:text-accent-foreground rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove media"
                data-testid={`media-delete-${asset.id}`}
            >
                <X size={12} />
            </button>
            <div className="px-2 py-1 text-[10px] text-muted-foreground truncate" title={asset.file_name}>{asset.file_name}</div>
        </div>
    );
}

function ContentEditor({ open, onOpenChange, editing, options, parentOptions, onSaved }) {
    const e0 = editing || {};
    const [form, setForm] = useState({
        title: e0.title || "",
        content_source: e0.content_source || "",
        topic: e0.topic || "",
        parent_content_id: e0.parent_content_id || "",
        platform: e0.platform || "",
        post_type: e0.post_type || "",
        status: e0.status || "Idea",
        post_copy: e0.post_copy || "",
        scheduled_for: e0.scheduled_for ? e0.scheduled_for.slice(0, 16) : "",
    });
    const [assets, setAssets] = useState([]);
    const [logs, setLogs] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    // Load existing media + publishing log for an edit-mode mount. This
    // effect runs once per mount (parent uses `key={editing?.id || 'new'}`
    // to remount on change), so the dependency list is intentionally empty.
    useEffect(() => {
        if (editing?.id) {
            api.get(`/content-studio/content/${editing.id}/media`).then((r) => setAssets(r.data || []));
            api.get(`/content-studio/publishing-log?content_id=${editing.id}`).then((r) => setLogs(r.data || []));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshLogs = async () => {
        if (!editing?.id) return;
        try {
            const r = await api.get(`/content-studio/publishing-log?content_id=${editing.id}`);
            setLogs(r.data || []);
        } catch { /* ignore — log fetch is best-effort */ }
    };

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async (overrides = {}) => {
        const merged = { ...form, ...overrides };
        if (!merged.title.trim()) { toast.error("Title is required"); return; }
        setSaving(true);
        try {
            const payload = {
                title: merged.title.trim(),
                content_source: merged.content_source || null,
                topic: merged.topic || null,
                parent_content_id: merged.parent_content_id || null,
                platform: merged.platform || null,
                post_type: merged.post_type || null,
                status: merged.status || "Idea",
                post_copy: merged.post_copy || "",
                scheduled_for: merged.scheduled_for ? new Date(merged.scheduled_for).toISOString() : null,
            };
            // Reflect the saved status in the editor's local state so the
            // status select doesn't snap back after a quick-save.
            if (overrides.status) setForm((f) => ({ ...f, status: overrides.status }));
            let saved;
            if (editing?.id) {
                const r = await api.patch(`/content-studio/content/${editing.id}`, payload);
                saved = r.data;
                toast.success("Saved");
            } else {
                const r = await api.post("/content-studio/content", payload);
                saved = r.data;
                toast.success("Created");
            }
            await onSaved(saved);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const uploadMedia = async (files) => {
        if (!files?.length) return;
        if (!editing?.id) {
            toast.error("Save the draft first, then add media");
            return;
        }
        setUploading(true);
        for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            try {
                const r = await api.post(`/content-studio/content/${editing.id}/media`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setAssets((a) => [...a, r.data]);
            } catch (e) {
                toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`);
            }
        }
        setUploading(false);
    };

    const deleteAsset = async (id) => {
        try {
            await api.delete(`/content-studio/media/${id}`);
            setAssets((a) => a.filter((x) => x.id !== id));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const quickSave = async (newStatus) => {
        await save({ status: newStatus });
    };

    const [aiBusy, setAiBusy] = useState(null);   // platform_key being generated, or "all"
    const [voiceProfile, setVoiceProfile] = useState(
        () => localStorage.getItem("haevn-cs-voice") || "founder"
    );

    const persistVoice = (v) => {
        setVoiceProfile(v);
        localStorage.setItem("haevn-cs-voice", v);
    };

    const generateOne = async (platform_key) => {
        if (!editing?.id) {
            toast.error("Save the draft first so we know what to generate from.");
            return;
        }
        setAiBusy(platform_key);
        try {
            const r = await api.post(
                `/content-studio/content/${editing.id}/generate-variant`,
                { platform_key, voice_profile: voiceProfile },
            );
            toast.success(`Generated ${r.data.created_count} ${platform_key} variant${r.data.created_count === 1 ? "" : "s"}`, {
                description: `Voice: ${voiceProfile}. Find them as children of this piece in the Library tab.`,
                action: { label: "Open Library", onClick: () => { localStorage.setItem("haevn-cs-tab", "library"); window.location.reload(); } },
                duration: 6000,
            });
            await onSaved(r.data.children?.[0]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI generation failed");
        } finally {
            setAiBusy(null);
        }
    };

    const generateAll = async () => {
        if (!editing?.id) {
            toast.error("Save the draft first so we know what to generate from.");
            return;
        }
        setAiBusy("all");
        const started = Date.now();
        try {
            const r = await api.post(
                `/content-studio/content/${editing.id}/generate-all`,
                { voice_profile: voiceProfile },
            );
            const took = ((Date.now() - started) / 1000).toFixed(1);
            const labels = (r.data.children || []).map((c) => c.content_type).join(" · ");
            toast.success(`Generated ${r.data.created_count} variants in ${took}s`, {
                description: labels || "Find them as children of this piece in the Library tab.",
                action: { label: "Open Library", onClick: () => { localStorage.setItem("haevn-cs-tab", "library"); window.location.reload(); } },
                duration: 9000,
            });
            if (r.data.error_count) {
                toast.warning(`${r.data.error_count} variant${r.data.error_count === 1 ? "" : "s"} failed`, {
                    description: (r.data.errors || []).map((e) => `${e.platform_key}: ${e.error}`).join("\n"),
                });
            }
            await onSaved(r.data.children?.[0]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI generation failed");
        } finally {
            setAiBusy(null);
        }
    };

    const aiPlaceholder = () => toast.message("Coming soon", { description: "This generator is on the roadmap." });

    const [sendingBuffer, setSendingBuffer] = useState(false);
    const sendToBuffer = async () => {
        if (!editing?.id) {
            toast.error("Save the draft first, then send to Buffer.");
            return;
        }
        if (!form.platform || !form.post_copy.trim()) {
            toast.error("Platform and post copy are required before sending to Buffer.");
            return;
        }
        setSendingBuffer(true);
        try {
            // Persist any pending edits BEFORE asking the backend to send —
            // otherwise the dropdown may show "Instagram" but the DB still
            // holds the old value, and validation fails with a confusing
            // "Platform is required" error. (Reproduced by user on 2026-06-10.)
            await api.patch(`/content-studio/content/${editing.id}`, {
                title: form.title.trim(),
                content_source: form.content_source || null,
                topic: form.topic || null,
                parent_content_id: form.parent_content_id || null,
                platform: form.platform || null,
                post_type: form.post_type || null,
                status: form.status || "Idea",
                post_copy: form.post_copy || "",
                scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null,
            });
            const r = await api.post(`/content-studio/content/${editing.id}/send-to-buffer`);
            await refreshLogs();
            if (r.data.ok) {
                toast.success(r.data.user_message || "Successfully queued in Buffer.", {
                    description: r.data.mock
                        ? `Mock mode — fake post id ${r.data.buffer_post_id}. Set a real Buffer key in Settings to publish for real.`
                        : `Buffer post id: ${r.data.buffer_post_id}`,
                });
                await onSaved(r.data.content);
                onOpenChange(false);
            } else {
                toast.error(r.data.user_message || "Buffer rejected this post. See logs for details.", {
                    description: r.data.message,
                });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to reach Buffer.");
        } finally {
            setSendingBuffer(false);
        }
    };

    const characterLimit = (p) => p === "TikTok" ? 2200 : p === "Instagram" ? 2200 : p === "Facebook" ? 63206 : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="content-editor">
                <DialogHeader>
                    <DialogTitle className="font-display">{editing?.id ? "Edit content" : "Create content"}</DialogTitle>
                    <DialogDescription className="text-[12px] text-muted-foreground">
                        {editing?.id
                            ? "Update fields, attach media, and move the piece through the lifecycle."
                            : "Fill in the basics — you can attach media and run AI tools after the first save."}
                    </DialogDescription>
                </DialogHeader>

                {/* Origin panel — shows source attribution when this Studio piece
                    was imported from a Content Idea. Read-only by design — the
                    fields are frozen at import time. */}
                {editing?.created_from && (
                    <section
                        data-testid="origin-panel"
                        className="border border-primary/30 bg-primary/5 rounded-sm px-4 py-3 mb-1"
                    >
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="overline text-[9px] text-primary flex items-center gap-1.5">
                                <Lightbulb size={11} weight="duotone" /> Origin · {editing.created_from}
                            </div>
                            {editing.created_from_id && (
                                <Button
                                    data-testid="origin-view-idea"
                                    variant="ghost" size="sm"
                                    className="rounded-sm h-7 px-2 text-[10px] font-mono uppercase tracking-wider text-primary hover:bg-primary/10"
                                    onClick={() => { window.location.href = `/content-ideas?highlight=${editing.created_from_id}`; }}
                                >
                                    View original idea →
                                </Button>
                            )}
                        </div>
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]">
                            {editing.source_title && (
                                <div className="md:col-span-2 flex gap-2">
                                    <dt className="overline text-[9px] tracking-wider min-w-[80px]">Source</dt>
                                    <dd className="italic text-muted-foreground leading-snug">{editing.source_title}</dd>
                                </div>
                            )}
                            {editing.stream_name && (
                                <div className="flex gap-2">
                                    <dt className="overline text-[9px] tracking-wider min-w-[80px]">Stream</dt>
                                    <dd className="text-foreground">{editing.stream_name}</dd>
                                </div>
                            )}
                            {editing.content_type && (
                                <div className="flex gap-2">
                                    <dt className="overline text-[9px] tracking-wider min-w-[80px]">Idea type</dt>
                                    <dd className="text-foreground">{editing.content_type}</dd>
                                </div>
                            )}
                            {editing.strength_score > 0 && (
                                <div className="flex gap-2">
                                    <dt className="overline text-[9px] tracking-wider min-w-[80px]">Strength</dt>
                                    <dd className="text-foreground tabular-nums">{editing.strength_score}</dd>
                                </div>
                            )}
                            {editing.resonance_score && (
                                <div className="flex gap-2">
                                    <dt className="overline text-[9px] tracking-wider min-w-[80px]">Resonance</dt>
                                    <dd className={`uppercase font-mono text-[10px] tracking-wider ${
                                        editing.resonance_score === "high" ? "text-accent"
                                            : editing.resonance_score === "medium" ? "text-foreground" : "text-muted-foreground"
                                    }`}>{editing.resonance_score}</dd>
                                </div>
                            )}
                            {editing.imported_at && (
                                <div className="flex gap-2">
                                    <dt className="overline text-[9px] tracking-wider min-w-[80px]">Imported</dt>
                                    <dd className="font-mono text-[11px] text-muted-foreground tabular-nums">{fmtDateTime(editing.imported_at)}</dd>
                                </div>
                            )}
                            {(editing.tags?.length || 0) > 0 && (
                                <div className="md:col-span-2 flex gap-2 flex-wrap items-baseline">
                                    <dt className="overline text-[9px] tracking-wider min-w-[80px]">Tags</dt>
                                    <dd className="flex flex-wrap gap-1">
                                        {editing.tags.slice(0, 8).map((t) => (
                                            <span key={t} className="text-[10px] px-1.5 py-0.5 bg-secondary rounded-sm">{t}</span>
                                        ))}
                                        {editing.tags.length > 8 && (
                                            <span className="text-[10px] text-muted-foreground italic">+{editing.tags.length - 8}</span>
                                        )}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </section>
                )}

                {/* Content Information */}
                <div className="space-y-5">
                    <section>
                        <div className="overline mb-2">Content information</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                                <Label className="overline text-[9px]">Title</Label>
                                <Input value={form.title} onChange={(e) => upd("title", e.target.value)} className="rounded-sm" data-testid="editor-title" />
                            </div>
                            <div>
                                <Label className="overline text-[9px]">Content source</Label>
                                <Select value={form.content_source || ""} onValueChange={(v) => upd("content_source", v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="editor-source"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        {options.content_sources.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="overline text-[9px]">Topic</Label>
                                <Select value={form.topic || ""} onValueChange={(v) => upd("topic", v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="editor-topic"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        {options.topics.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="md:col-span-2">
                                <Label className="overline text-[9px]">Parent content (optional)</Label>
                                <Select value={form.parent_content_id || "__none__"} onValueChange={(v) => upd("parent_content_id", v === "__none__" ? "" : v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="editor-parent"><SelectValue placeholder="None (this is a root piece)" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">None (root)</SelectItem>
                                        {parentOptions.filter((p) => p.id !== editing?.id).map((p) => (
                                            <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="text-[10px] text-muted-foreground italic mt-1">
                                    Link a long-form piece to the social assets it spawned (e.g., article → IG carousel).
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Publishing Information */}
                    <section>
                        <div className="overline mb-2">Publishing information</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <Label className="overline text-[9px]">Platform</Label>
                                <Select value={form.platform || ""} onValueChange={(v) => upd("platform", v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="editor-platform"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        {options.platforms.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="overline text-[9px]">Post type</Label>
                                <Select value={form.post_type || ""} onValueChange={(v) => upd("post_type", v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="editor-posttype"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        {options.post_types.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="overline text-[9px]">Status</Label>
                                <Select value={form.status || "Idea"} onValueChange={(v) => upd("status", v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="editor-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {options.statuses.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="md:col-span-3">
                                <Label className="overline text-[9px]">Scheduled for (optional, used when status = Queued)</Label>
                                <Input
                                    type="datetime-local"
                                    value={form.scheduled_for || ""}
                                    onChange={(e) => upd("scheduled_for", e.target.value)}
                                    className="rounded-sm w-full md:w-72"
                                    data-testid="editor-scheduled"
                                />
                            </div>
                        </div>
                    </section>

                    {/* Post copy */}
                    <section>
                        <div className="overline mb-2 flex items-center justify-between">
                            <span>Post copy</span>
                            <CharCount value={form.post_copy} limit={characterLimit(form.platform)} />
                        </div>
                        <Textarea
                            value={form.post_copy}
                            onChange={(e) => upd("post_copy", e.target.value)}
                            rows={9}
                            className="rounded-sm text-sm leading-relaxed"
                            placeholder="Write the post copy here. Mention key behaviors early."
                            data-testid="editor-copy"
                        />
                    </section>

                    {/* Media */}
                    <section>
                        <div className="overline mb-2 flex items-center justify-between">
                            <span>Media · {assets.length}</span>
                            <Button
                                type="button" size="sm" variant="outline"
                                className="rounded-sm h-7 text-[11px]"
                                onClick={() => fileRef.current?.click()}
                                disabled={!editing?.id || uploading}
                                data-testid="editor-add-media"
                            >
                                {uploading
                                    ? <><ArrowsClockwise size={11} className="mr-1 animate-spin" /> Uploading…</>
                                    : <><UploadSimple size={11} className="mr-1" /> Add image / video</>}
                            </Button>
                            <input
                                ref={fileRef}
                                type="file"
                                multiple
                                accept="image/*,video/*"
                                className="hidden"
                                onChange={(e) => { uploadMedia(Array.from(e.target.files)); e.target.value = ""; }}
                                data-testid="editor-media-input"
                            />
                        </div>
                        {!editing?.id ? (
                            <div className="border border-dashed border-border rounded-sm py-6 text-center text-[12px] italic text-muted-foreground">
                                Save the draft first, then add media.
                            </div>
                        ) : assets.length === 0 ? (
                            <div className="border border-dashed border-border rounded-sm py-6 text-center text-[12px] italic text-muted-foreground">
                                No media attached yet.
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                {assets.map((a) => <MediaTile key={a.id} asset={a} onDelete={deleteAsset} />)}
                            </div>
                        )}
                    </section>

                    {/* AI tools — real generators (Phase 4) */}
                    <section data-testid="ai-tools-section">
                        <div className="overline mb-2 flex items-center justify-between gap-2">
                            <span className="flex items-center gap-1.5">
                                <Sparkle size={11} weight="fill" className="text-primary" />
                                AI generators · HAEVN voice
                            </span>
                            <Select value={voiceProfile} onValueChange={persistVoice}>
                                <SelectTrigger className="rounded-sm h-7 text-[11px] w-44" data-testid="ai-voice-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="founder">Founder voice (default)</SelectItem>
                                    <SelectItem value="educational">Educational</SelectItem>
                                    <SelectItem value="contrarian">Contrarian</SelectItem>
                                    <SelectItem value="local">Local community</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-wrap gap-1.5 items-center mb-2">
                            <Button
                                type="button" size="sm"
                                className="rounded-sm h-8 text-[11px] font-mono uppercase tracking-wider"
                                onClick={generateAll}
                                disabled={!editing?.id || aiBusy !== null}
                                data-testid="ai-generate-all"
                                title="Master button — generates Facebook, LinkedIn, Instagram, Carousel and Video Script variants in parallel. Under 30 seconds."
                            >
                                {aiBusy === "all"
                                    ? <><ArrowsClockwise size={12} className="mr-1 animate-spin" /> Generating 7 variants…</>
                                    : <><Sparkle size={12} weight="fill" className="mr-1" /> Generate Content (all platforms)</>}
                            </Button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {[
                                ["Facebook",    "Generate Facebook Post"],
                                ["LinkedIn",    "Generate LinkedIn Post"],
                                ["Instagram",   "Generate Instagram Caption"],
                                ["Carousel",    "Generate Carousel"],
                                ["VideoScript", "Generate Video Script"],
                                ["Threads",     "Generate Threads Post"],
                                ["YouTube",     "Generate YouTube Metadata"],
                            ].map(([key, label]) => (
                                <Button
                                    key={key} type="button" size="sm" variant="outline"
                                    className="rounded-sm h-7 text-[11px]"
                                    onClick={() => generateOne(key)}
                                    disabled={!editing?.id || aiBusy !== null}
                                    data-testid={`ai-generate-${key.toLowerCase()}`}
                                >
                                    {aiBusy === key
                                        ? <><ArrowsClockwise size={11} className="mr-1 animate-spin" /> Generating…</>
                                        : <><Sparkle size={11} className="mr-1" /> {label}</>}
                                </Button>
                            ))}
                        </div>
                        <div className="text-[10px] italic text-muted-foreground mt-2">
                            Each variant is saved as a child Studio item — separate edit history, status, media, and Buffer flow.
                            {!editing?.id && " Save the draft first to enable."}
                        </div>
                    </section>

                    {/* Publishing activity log */}
                    {editing?.id && (
                        <section data-testid="editor-activity-log">
                            <div className="overline mb-2 flex items-center gap-1.5">
                                <PaperPlaneTilt size={11} /> Publishing activity · {logs.length}
                            </div>
                            {logs.length === 0 ? (
                                <div className="border border-dashed border-border rounded-sm py-4 text-center text-[12px] italic text-muted-foreground">
                                    No Buffer activity yet for this piece.
                                </div>
                            ) : (
                                <ul className="border border-border bg-card rounded-sm divide-y divide-border max-h-64 overflow-y-auto">
                                    {logs.map((l) => (
                                        <li key={l.id} className="px-3 py-2 flex items-start gap-3 text-[12px]" data-testid={`log-${l.id}`}>
                                            <BufferStatusChip status={l.result} />
                                            <div className="flex-1 min-w-0">
                                                <div className="truncate">{l.message || "—"}</div>
                                                <div className="text-[10px] text-muted-foreground font-mono">
                                                    {l.platform || "—"} · {l.action} · {fmtDateTime(l.timestamp)}
                                                    {l.buffer_post_id && <> · post {l.buffer_post_id.slice(0, 14)}</>}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    )}
                </div>

                <DialogFooter className="gap-1.5 flex-wrap">
                    <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
                    <Button type="button" variant="outline" className="rounded-sm" onClick={() => quickSave("Draft")} disabled={saving} data-testid="editor-save-draft">
                        Save Draft
                    </Button>
                    <Button type="button" variant="outline" className="rounded-sm" onClick={() => quickSave("Approved")} disabled={saving || !editing?.id} data-testid="editor-mark-approved">
                        Mark Approved
                    </Button>
                    <Button type="button" variant="outline" className="rounded-sm" onClick={() => quickSave("Queued")} disabled={saving || !editing?.id} data-testid="editor-queue">
                        Queue Post
                    </Button>
                    <Button type="button" className="rounded-sm" onClick={sendToBuffer} disabled={sendingBuffer || !editing?.id} data-testid="editor-buffer">
                        {sendingBuffer
                            ? <><ArrowsClockwise size={12} className="mr-1 animate-spin" /> Sending…</>
                            : <><PaperPlaneTilt size={12} className="mr-1" /> Send to Buffer</>}
                    </Button>
                    <Button type="button" className="rounded-sm" onClick={save} disabled={saving} data-testid="editor-save">
                        {saving ? <ArrowsClockwise size={12} className="mr-1 animate-spin" /> : null}
                        {editing?.id ? "Save changes" : "Create"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Queue table tab ----------

function QueueTab({ rows, options, onEdit, onDuplicate, onDelete, openCreate, filters, setFilters }) {
    const filtered = useMemo(() => {
        const q = (filters.search || "").trim().toLowerCase();
        return rows.filter((r) => {
            if (filters.platform !== "all" && r.platform !== filters.platform) return false;
            if (filters.topic !== "all" && r.topic !== filters.topic) return false;
            if (filters.status !== "all" && r.status !== filters.status) return false;
            if (q && !(r.title || "").toLowerCase().includes(q) && !(r.post_copy || "").toLowerCase().includes(q)) return false;
            return true;
        });
    }, [rows, filters]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-2 justify-between">
                <div className="flex flex-wrap gap-2 items-center">
                    <div className="relative">
                        <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            placeholder="Search title or copy…"
                            className="rounded-sm h-8 pl-7 text-xs w-56"
                            data-testid="queue-search"
                        />
                    </div>
                    <Select value={filters.platform} onValueChange={(v) => setFilters({ ...filters, platform: v })}>
                        <SelectTrigger className="rounded-sm h-8 text-xs w-36" data-testid="queue-filter-platform"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All platforms</SelectItem>
                            {options.platforms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filters.topic} onValueChange={(v) => setFilters({ ...filters, topic: v })}>
                        <SelectTrigger className="rounded-sm h-8 text-xs w-36" data-testid="queue-filter-topic"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All topics</SelectItem>
                            {options.topics.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                        <SelectTrigger className="rounded-sm h-8 text-xs w-36" data-testid="queue-filter-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {options.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <Button size="sm" className="rounded-sm h-8 text-[11px]" onClick={openCreate} data-testid="queue-create-btn">
                    <Plus size={12} className="mr-1" /> New content
                </Button>
            </div>

            {filtered.length === 0 ? (
                <div className="border border-dashed border-border rounded-sm py-12 text-center text-[13px] italic text-muted-foreground">
                    No content matches your filters. {rows.length === 0 && <span>Start with <span className="not-italic">New content</span> above.</span>}
                </div>
            ) : (
                <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
                    <table className="w-full text-[12px] min-w-[1700px]">
                        <thead className="border-b border-border bg-muted/30">
                            <tr>
                                <th className="text-left overline text-[9px] px-3 py-2 w-24">Created</th>
                                <th className="text-left overline text-[9px] px-3 py-2">Title</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-28">Platform</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-28">Post type</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-32">Topic</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-24">Status</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-28">Source</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-40">Original idea</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-32">Stream</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-28">Imported</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-24">Buffer status</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-32">Buffer post ID</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-28">Last sync</th>
                                <th className="text-left overline text-[9px] px-3 py-2 w-24">Published</th>
                                <th className="text-right overline text-[9px] px-3 py-2 w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-muted/20 cursor-pointer" data-testid={`queue-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground tabular-nums" onClick={() => onEdit(r)}>{fmtDate(r.created_at)}</td>
                                    <td className="px-3 py-2 font-display" onClick={() => onEdit(r)}>
                                        <div className="truncate max-w-[280px]" title={r.title}>{r.title}</div>
                                        {r.media_asset_ids?.length > 0 && (
                                            <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                                                <ImageSquare size={10} /> {r.media_asset_ids.length} media
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2" onClick={() => onEdit(r)}><PlatformChip platform={r.platform} /></td>
                                    <td className="px-3 py-2" onClick={() => onEdit(r)}>{r.post_type || <span className="text-muted-foreground italic">—</span>}</td>
                                    <td className="px-3 py-2" onClick={() => onEdit(r)}>{r.topic || <span className="text-muted-foreground italic">—</span>}</td>
                                    <td className="px-3 py-2" onClick={() => onEdit(r)}><StatusChip status={r.status} /></td>
                                    <td className="px-3 py-2" onClick={() => onEdit(r)}>
                                        {r.created_from
                                            ? <span className="inline-flex items-center gap-1 text-[11px]"><Lightbulb size={10} weight="duotone" className="text-primary" />{r.created_from}</span>
                                            : (r.content_source || <span className="text-muted-foreground italic">—</span>)}
                                    </td>
                                    <td className="px-3 py-2" onClick={() => onEdit(r)}>
                                        {r.created_from_id
                                            ? <a
                                                href={`/content-ideas?highlight=${r.created_from_id}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-primary hover:underline truncate inline-block max-w-[160px]"
                                                title={r.source_title || r.title}
                                              >
                                                  {(r.source_title || r.title || "—").slice(0, 30)}{(r.source_title || r.title || "").length > 30 ? "…" : ""}
                                              </a>
                                            : <span className="text-muted-foreground italic">—</span>}
                                    </td>
                                    <td className="px-3 py-2" onClick={() => onEdit(r)}>{r.stream_name || <span className="text-muted-foreground italic">—</span>}</td>
                                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground tabular-nums" onClick={() => onEdit(r)}>{fmtDateTime(r.imported_at)}</td>
                                    <td className="px-3 py-2" onClick={() => onEdit(r)}><BufferStatusChip status={r.buffer_status} /></td>
                                    <td className="px-3 py-2 font-mono text-[10px]" onClick={() => onEdit(r)}>
                                        {r.buffer_post_id
                                            ? <span title={r.buffer_post_id} className="truncate inline-block max-w-[120px]">{r.buffer_post_id}</span>
                                            : <span className="text-muted-foreground italic">—</span>}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground tabular-nums" onClick={() => onEdit(r)}>{fmtDateTime(r.buffer_last_sync)}</td>
                                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground tabular-nums" onClick={() => onEdit(r)}>{fmtDate(r.published_at)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <button onClick={(e) => { e.stopPropagation(); onEdit(r); }} className="text-muted-foreground hover:text-foreground mr-1.5" title="Edit" data-testid={`queue-edit-${r.id}`}>
                                            <Pencil size={11} />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); onDuplicate(r); }} className="text-muted-foreground hover:text-foreground mr-1.5" title="Duplicate" data-testid={`queue-dup-${r.id}`}>
                                            <Copy size={11} />
                                        </button>
                                        <button onClick={(e) => { e.stopPropagation(); onDelete(r); }} className="text-muted-foreground hover:text-accent" title="Delete" data-testid={`queue-del-${r.id}`}>
                                            <Trash size={11} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ---------- Library tab ----------

function LibraryTab({ rows, options, onEdit }) {
    const [filters, setFilters] = useState({ search: "", topic: "all", platform: "all", source: "all", status: "all" });

    const filtered = useMemo(() => {
        const q = (filters.search || "").trim().toLowerCase();
        return rows.filter((r) => {
            if (filters.platform !== "all" && r.platform !== filters.platform) return false;
            if (filters.topic !== "all" && r.topic !== filters.topic) return false;
            if (filters.source !== "all" && r.content_source !== filters.source) return false;
            if (filters.status !== "all" && r.status !== filters.status) return false;
            if (q && !(r.title || "").toLowerCase().includes(q) && !(r.post_copy || "").toLowerCase().includes(q)) return false;
            return true;
        });
    }, [rows, filters]);

    // Parent → children grouping. Items without a parent are roots; items
    // whose parent matches a root row are children of that root.
    const tree = useMemo(() => {
        const byId = Object.fromEntries(rows.map((r) => [r.id, r]));
        const roots = [];
        const childMap = {};
        for (const r of filtered) {
            if (r.parent_content_id && byId[r.parent_content_id]) {
                (childMap[r.parent_content_id] = childMap[r.parent_content_id] || []).push(r);
            } else {
                roots.push(r);
            }
        }
        // Ensure parents of filtered children are visible even if their own
        // fields didn't match the filter — keeps the tree readable.
        for (const pid of Object.keys(childMap)) {
            if (!roots.find((r) => r.id === pid)) {
                const p = byId[pid];
                if (p) roots.push(p);
            }
        }
        roots.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        return { roots, childMap };
    }, [rows, filtered]);

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
                <div className="relative">
                    <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        placeholder="Search all content…"
                        className="rounded-sm h-8 pl-7 text-xs w-64"
                        data-testid="lib-search"
                    />
                </div>
                <Select value={filters.topic} onValueChange={(v) => setFilters({ ...filters, topic: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-32" data-testid="lib-topic"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All topics</SelectItem>
                        {options.topics.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filters.platform} onValueChange={(v) => setFilters({ ...filters, platform: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-36" data-testid="lib-platform"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All platforms</SelectItem>
                        {options.platforms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-40" data-testid="lib-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All sources</SelectItem>
                        {options.content_sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-32" data-testid="lib-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {options.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {tree.roots.length === 0 ? (
                <div className="border border-dashed border-border rounded-sm py-12 text-center text-[13px] italic text-muted-foreground">
                    No content matches your filters.
                </div>
            ) : (
                <ul className="space-y-2">
                    {tree.roots.map((r) => {
                        const children = tree.childMap[r.id] || [];
                        return (
                            <li key={r.id} className="border border-border bg-card rounded-sm">
                                <div className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30" onClick={() => onEdit(r)} data-testid={`lib-row-${r.id}`}>
                                    <StatusChip status={r.status} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-semibold text-[14px] truncate">{r.title}</div>
                                        <div className="text-[11px] text-muted-foreground">
                                            {r.content_source || "—"} · {r.topic || "—"} · updated {fmtDateTime(r.updated_at)}
                                        </div>
                                    </div>
                                    <PlatformChip platform={r.platform} />
                                    <span className="text-[11px] text-muted-foreground">{r.post_type || "—"}</span>
                                </div>
                                {children.length > 0 && (
                                    <ul className="border-t border-border bg-muted/20 divide-y divide-border">
                                        {children.map((c) => (
                                            <li key={c.id} className="px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-muted/40" onClick={() => onEdit(c)} data-testid={`lib-child-${c.id}`}>
                                                <span className="text-muted-foreground text-[12px]">↳</span>
                                                <StatusChip status={c.status} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[13px] truncate">{c.title}</div>
                                                </div>
                                                <PlatformChip platform={c.platform} />
                                                <span className="text-[11px] text-muted-foreground">{c.post_type || "—"}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

// ---------- Settings tab ----------

function SettingsField({ k, label, mono = false, value, onChange }) {
    return (
        <div>
            <Label className="overline text-[9px]">{label}</Label>
            <Input
                value={value || ""}
                onChange={(e) => onChange(k, e.target.value)}
                className={`rounded-sm ${mono ? "font-mono text-xs" : ""}`}
                data-testid={`settings-${k}`}
                placeholder="—"
            />
        </div>
    );
}

function SettingsTab() {
    const [s, setS] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        api.get("/content-studio/settings").then((r) => { setS(r.data); setForm(r.data); });
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.patch("/content-studio/settings", form);
            setS(r.data); setForm(r.data);
            toast.success("Settings saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            // Persist any unsaved key first so the backend tests the latest value.
            await api.patch("/content-studio/settings", form);
            const r = await api.post("/content-studio/settings/test-buffer");
            setTestResult(r.data);
            if (r.data.ok) {
                toast.success(r.data.mock ? "Connected (mock mode)" : "Connected to Buffer", {
                    description: r.data.message,
                });
            } else {
                toast.error("Buffer connection failed", { description: r.data.message });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Connection test failed");
        } finally {
            setTesting(false);
        }
    };

    const [detecting, setDetecting] = useState(false);
    const detectChannels = async () => {
        setDetecting(true);
        try {
            // Save any pending API-key edit BEFORE calling — the detect
            // endpoint reads the persisted key.
            await api.patch("/content-studio/settings", form);
            const r = await api.post("/content-studio/settings/discover-buffer-channels", { apply: true });
            const d = r.data || {};
            if (!d.ok) {
                toast.error("Detect channels failed", { description: d.message });
                return;
            }
            const applied = d.applied || {};
            const keys = Object.keys(applied);
            if (keys.length === 0) {
                toast.success("Channels already up-to-date", { description: d.message });
            } else {
                const desc = keys.map((k) => {
                    const v = applied[k];
                    if (typeof v === "object" && v.channel_name) return `${k.replace("_channel_id", "")} → ${v.channel_name}`;
                    return k;
                }).join(", ");
                toast.success(`Synced ${keys.length} channel${keys.length === 1 ? "" : "s"} from Buffer`, {
                    description: desc, duration: 9000,
                });
            }
            // Reload settings so the fields update in the UI
            const fresh = await api.get("/content-studio/settings");
            setS(fresh.data);
            setForm(fresh.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Detect channels failed");
        } finally {
            setDetecting(false);
        }
    };

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    if (!s) return <div className="text-sm text-muted-foreground">Loading…</div>;

    return (
        <div className="space-y-8">
            <div className="max-w-4xl">
                <KnowledgeBaseCard />
                <SocialTemplatesCard />
                <BrandProfileCard />
            </div>
            <div className="space-y-8 max-w-2xl">
            <section>
                <div className="overline mb-3">Buffer integration</div>
                <p className="editorial text-[12px] text-muted-foreground mb-4 italic leading-relaxed">
                    Stored for Version 1 — the actual Buffer publishing flow lands in a future release. Add your keys now
                    so the integration switches on automatically when it ships.
                </p>

                <div className="mb-5">
                    <BufferChannelsPanel onDetect={detectChannels} />
                </div>

                <div className="space-y-3">
                    <SettingsField k="buffer_api_key" label="Buffer API key" mono value={form.buffer_api_key} onChange={setField} />
                    <SettingsField k="buffer_organization_id" label="Buffer organization ID" mono value={form.buffer_organization_id} onChange={setField} />
                </div>
            </section>

            <section>
                <div className="overline mb-3">Channel IDs</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <SettingsField k="facebook_channel_id" label="Facebook channel ID" mono value={form.facebook_channel_id} onChange={setField} />
                    <SettingsField k="instagram_channel_id" label="Instagram channel ID" mono value={form.instagram_channel_id} onChange={setField} />
                    <SettingsField k="tiktok_channel_id" label="TikTok channel ID" mono value={form.tiktok_channel_id} onChange={setField} />
                    <SettingsField k="linkedin_channel_id" label="LinkedIn channel ID" mono value={form.linkedin_channel_id} onChange={setField} />
                    <SettingsField k="threads_channel_id" label="Threads channel ID" mono value={form.threads_channel_id} onChange={setField} />
                    <SettingsField k="youtube_channel_id" label="YouTube channel ID" mono value={form.youtube_channel_id} onChange={setField} />
                </div>
            </section>

            <div className="pt-2 border-t border-border flex flex-wrap gap-2 items-center">
                <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="settings-save-btn">
                    {saving ? <ArrowsClockwise size={12} className="mr-1 animate-spin" /> : null}
                    Save settings
                </Button>
                <Button variant="outline" onClick={testConnection} disabled={testing} className="rounded-sm" data-testid="settings-test-buffer-btn">
                    {testing
                        ? <><ArrowsClockwise size={12} className="mr-1 animate-spin" /> Testing…</>
                        : <><PaperPlaneTilt size={12} className="mr-1" /> Test Buffer connection</>}
                </Button>
                <Button
                    variant="outline"
                    onClick={detectChannels}
                    disabled={detecting || !form.buffer_api_key}
                    className="rounded-sm"
                    data-testid="settings-detect-channels-btn"
                    title={!form.buffer_api_key ? "Add your Buffer API key first" : "Read the connected channels from Buffer and auto-fill the channel IDs above"}
                >
                    {detecting
                        ? <><ArrowsClockwise size={12} className="mr-1 animate-spin" /> Detecting…</>
                        : <><MagnifyingGlass size={12} className="mr-1" /> Detect channels from Buffer</>}
                </Button>
                {s.updated_at && (
                    <span className="ml-1 text-[11px] text-muted-foreground italic">
                        Last updated {fmtDateTime(s.updated_at)}
                    </span>
                )}
            </div>

            {testResult && (
                <div
                    data-testid="settings-test-result"
                    className={`mt-3 border rounded-sm px-3 py-2 text-[12px] ${
                        testResult.ok
                            ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-900 dark:text-emerald-200"
                            : "border-red-500/40 bg-red-500/5 text-red-900 dark:text-red-200"
                    }`}
                >
                    <span className="font-mono uppercase tracking-wider text-[10px] mr-2">
                        {testResult.ok ? (testResult.mock ? "MOCK OK" : "OK") : "FAILED"}
                    </span>
                    {testResult.message}
                    {testResult.viewer?.email && (
                        <span className="ml-2 font-mono text-[11px] opacity-80">· {testResult.viewer.email}</span>
                    )}
                </div>
            )}
            </div>
        </div>
    );
}

// ---------- Main page with tabs ----------

const TABS = [
    { key: "dashboard",      label: "Dashboard" },
    { key: "production-line", label: "Production Line" },
    { key: "sources",        label: "Sources" },
    { key: "queue",          label: "Content Queue" },
    { key: "library",        label: "Content Library" },
    { key: "settings",       label: "Settings" },
];

export default function ContentStudio() {
    const [tab, setTab] = useState(() => localStorage.getItem("haevn-cs-tab") || "dashboard");
    const [ideaFilter, setIdeaFilter] = useState(() => localStorage.getItem("haevn-cs-idea-filter") || "");
    const [dashData, setDashData] = useState(null);
    const [rows, setRows] = useState([]);
    const [options, setOptions] = useState({
        content_sources: [], topics: [], platforms: [], post_types: [], statuses: [],
    });
    const [loading, setLoading] = useState(true);
    const [editor, setEditor] = useState({ open: false, editing: null });
    const [filters, setFilters] = useState({ search: "", platform: "all", topic: "all", status: "all" });

    useEffect(() => { localStorage.setItem("haevn-cs-tab", tab); }, [tab]);

    // Phase 5.5: when arriving from a Content Idea's "In Studio" badge,
    // remember which idea we should be filtering on. Clearing the filter
    // also clears the localStorage flag so revisits start clean.
    const clearIdeaFilter = () => {
        setIdeaFilter("");
        localStorage.removeItem("haevn-cs-idea-filter");
    };

    const filteredRows = ideaFilter
        ? rows.filter((r) => r.source_idea_id === ideaFilter || r.created_from_id === ideaFilter)
        : rows;

    const reload = async () => {
        try {
            const [optR, dashR, listR] = await Promise.all([
                api.get("/content-studio/options"),
                api.get("/content-studio/dashboard"),
                api.get("/content-studio/content?limit=500"),
            ]);
            setOptions(optR.data);
            setDashData(dashR.data);
            setRows(listR.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    const openCreate = () => setEditor({ open: true, editing: null });
    const openEdit = (row) => setEditor({ open: true, editing: row });

    const jumpToItem = (id) => {
        const r = rows.find((x) => x.id === id);
        if (r) openEdit(r);
        setTab("queue");
    };

    const onSaved = async () => { await reload(); };

    const duplicate = async (row) => {
        try {
            await api.post(`/content-studio/content/${row.id}/duplicate`);
            toast.success("Duplicated");
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Duplicate failed");
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete "${row.title}"? Media files will also be removed.`)) return;
        try {
            await api.delete(`/content-studio/content/${row.id}`);
            toast.success("Deleted");
            await reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <>
            <header className="px-8 lg:px-14 pt-12 pb-4 border-b border-border">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div className="max-w-[68ch]">
                        <div className="overline mb-3 text-primary">HAEVN Labs · Internal</div>
                        <h1 className="font-display font-black tracking-[-0.025em] leading-[0.95] text-[clamp(2rem,3.4vw,2.8rem)]">
                            Content Studio
                        </h1>
                        <p className="editorial text-muted-foreground mt-3 leading-relaxed">
                            HAEVN&apos;s internal content operating system. Move pieces from idea through approval, queue,
                            and publication — with media, parent/child linking, and Buffer-ready scheduling.
                        </p>
                    </div>
                </div>

                <nav className="mt-6 flex gap-1" data-testid="cs-tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`cs-tab-${t.key}`}
                            className={`px-3.5 h-9 rounded-sm text-[12px] uppercase tracking-wider border-b-2 transition-colors -mb-px ${
                                tab === t.key
                                    ? "border-primary text-foreground font-display font-semibold"
                                    : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>
            </header>

            <main className="px-8 lg:px-14 py-10 max-w-[1300px] mx-auto" data-testid="cs-main">
                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading Content Studio…</div>
                ) : (
                    <>
                        {tab === "dashboard"       && <DashboardTab data={dashData} onJump={jumpToItem} />}
                        {tab === "production-line" && <ProductionLine />}
                        {tab === "sources"         && <SourcesTab />}
                        {tab === "queue"     && (
                            <>
                                {ideaFilter && (
                                    <div
                                        data-testid="cs-idea-filter-banner"
                                        className="mb-4 px-3 py-2 border border-primary/30 bg-primary/5 rounded-sm flex items-center justify-between gap-3"
                                    >
                                        <span className="text-[12px] text-foreground">
                                            Showing only Content Studio items spawned from a single Content Idea.
                                            <span className="ml-2 font-mono text-[11px] text-muted-foreground">
                                                ({filteredRows.length} variant{filteredRows.length === 1 ? "" : "s"})
                                            </span>
                                        </span>
                                        <button
                                            type="button"
                                            data-testid="cs-clear-idea-filter"
                                            onClick={clearIdeaFilter}
                                            className="text-[11px] font-mono uppercase tracking-wider text-primary hover:underline"
                                        >
                                            Clear filter
                                        </button>
                                    </div>
                                )}
                                <QueueTab
                                    rows={filteredRows} options={options}
                                    onEdit={openEdit} onDuplicate={duplicate} onDelete={remove}
                                    openCreate={openCreate}
                                    filters={filters} setFilters={setFilters}
                                />
                            </>
                        )}
                        {tab === "library"   && <LibraryTab rows={rows} options={options} onEdit={openEdit} />}
                        {tab === "settings"  && <SettingsTab />}
                    </>
                )}
            </main>

            <ContentEditor
                key={editor.editing?.id || (editor.open ? "new" : "closed")}
                open={editor.open}
                onOpenChange={(o) => setEditor({ open: o, editing: o ? editor.editing : null })}
                editing={editor.editing}
                options={options}
                parentOptions={rows}
                onSaved={onSaved}
            />
        </>
    );
}
